import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, MagnifyingGlass, User } from '@phosphor-icons/react';
import { formatDate } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    aadhaar: '',
    pan: '',
    address: ''
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async (searchTerm = '') => {
    try {
      const params = searchTerm ? { search: searchTerm } : {};
      const { data } = await axios.get(`${API_URL}/api/customers`, {
        params,
        withCredentials: true
      });
      setCustomers(data);
    } catch (error) {
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    fetchCustomers(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/customers`, formData, {
        withCredentials: true
      });
      toast.success('Customer created successfully');
      setShowDialog(false);
      setFormData({ name: '', email: '', phone: '', aadhaar: '', pan: '', address: '' });
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to create customer');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C9A227]"></div>
      </div>
    );
  }

  return (
    <div data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-4xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Customers
          </h1>
          <p className="text-[#6B7280] mt-2">Manage your customer database</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[#C9A227] hover:bg-[#A9851F] mt-4 md:mt-0" data-testid="add-customer-button">
              <Plus size={16} className="mr-2" weight="bold" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Customer</DialogTitle>
              <DialogDescription>Enter customer details including KYC information</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="customer-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="customer-phone-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="email">Email (Optional)</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  data-testid="customer-email-input"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="aadhaar">Aadhaar Number</Label>
                  <Input
                    id="aadhaar"
                    name="aadhaar"
                    value={formData.aadhaar}
                    onChange={handleChange}
                    data-testid="customer-aadhaar-input"
                  />
                </div>
                <div>
                  <Label htmlFor="pan">PAN Number</Label>
                  <Input
                    id="pan"
                    name="pan"
                    value={formData.pan}
                    onChange={handleChange}
                    data-testid="customer-pan-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  data-testid="customer-address-input"
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#C9A227] hover:bg-[#A9851F]" data-testid="submit-customer-button">
                  Create Customer
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg border border-[#E5E7EB] p-6">
        <div className="mb-4">
          <div className="relative">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#6B7280]" />
            <Input
              type="text"
              placeholder="Search by name, phone, or email..."
              value={search}
              onChange={handleSearch}
              className="pl-10"
              data-testid="search-customers-input"
            />
          </div>
        </div>

        {customers.length === 0 ? (
          <div className="text-center py-12">
            <User size={48} className="mx-auto text-[#6B7280] mb-4" weight="duotone" />
            <p className="text-[#6B7280]">No customers found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-left">
                  <th className="pb-3 text-sm font-semibold text-[#1A1A1A]">Name</th>
                  <th className="pb-3 text-sm font-semibold text-[#1A1A1A]">Phone</th>
                  <th className="pb-3 text-sm font-semibold text-[#1A1A1A]">Email</th>
                  <th className="pb-3 text-sm font-semibold text-[#1A1A1A]">KYC</th>
                  <th className="pb-3 text-sm font-semibold text-[#1A1A1A]">Created</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr 
                    key={customer.id} 
                    className="border-b border-[#E5E7EB] hover:bg-[#F9F9FB] transition-colors"
                    data-testid={`customer-row-${customer.id}`}
                  >
                    <td className="py-3 font-medium text-[#1A1A1A]">{customer.name}</td>
                    <td className="py-3 text-[#6B7280] font-mono-num" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {customer.phone}
                    </td>
                    <td className="py-3 text-[#6B7280]">{customer.email || '-'}</td>
                    <td className="py-3">
                      {customer.aadhaar || customer.pan ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-green-50 text-green-700 text-xs border border-green-200">
                          Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-yellow-50 text-yellow-700 text-xs border border-yellow-200">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-[#6B7280] text-sm">
                      {customer.created_at ? formatDate(customer.created_at) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Customers;
